import React from 'react'
import classNames from 'classnames'
import {computeAge} from '../functions/memberDisplay'
import GenderIcon from './GenderIcon'
import PriestBadge from './PriestBadge'
import ReligiousBadge from './ReligiousBadge'

// 1 ô người trong Cây Gia Phả - DÙNG CHUNG giữa 2 renderer (CSS ul/li ở FamilyBooks.js và toạ độ SVG
// ở SvgPedigreeTree.js), tách riêng ra đây để 2 cách vẽ luôn hiện ĐÚNG 1 kiểu ô (không lệch style khi
// sửa 1 bên quên sửa bên kia).
//
// ĐÃ THỬ bản card lớn hơn (căn trái, thêm nhãn vai trò quan hệ + nút bút chì sửa nhanh) theo 1 mẫu
// tham khảo - phản hồi thực tế "vẫn muốn như cũ, không lấy lần chỉnh đó" - quay lại ĐÚNG bản gọn/căn
// giữa này, không thêm nhãn vai trò/nút sửa nữa.

export const PEDIGREE_BOX_WIDTH = 84
// Bề rộng ô khi xoay chữ 90° (writing-mode: vertical-rl) - hẹp hơn khung ngang bình thường (84px)
// nhưng KHÔNG hẹp quá mức - đã thử 34px kèm chữ text-3xs (8.4px) và bị phản hồi khó đọc. Gốc vấn đề
// là cỡ chữ quá nhỏ chứ không phải do xoay (chữ Latin/Việt ở vertical-rl vẫn đứng thẳng, chỉ xếp
// chồng theo chiều dọc - không lật nghiêng từng ký tự), nên tăng thẳng cỡ chữ lên text-xs (12px,
// bằng cỡ chữ thường của app) + nới khung rộng hơn tương ứng để đủ chỗ thở cho dấu tiếng Việt.
export const PEDIGREE_BOX_WIDTH_ROTATED = 52
// Giới hạn chiều cao cột chữ dọc - ứng với ~13-14 ký tự ở cỡ text-xs trước khi cắt bớt (ellipsis),
// đủ cho phần lớn tên người Việt hiển thị trọn vẹn, tên quá dài mới bị cắt (xem title= để đọc đủ).
export const PEDIGREE_ROTATED_TEXT_MAX_HEIGHT = 170

// `tinted`: true để tô nền family-100 (nhạt, không chói) - CHỈ dùng cho primary ở các lần XUẤT HIỆN
// LẶP LẠI (từ cuộc hôn nhân thứ 2 trở đi, xem PedigreeNode) khi 1 người có NHIỀU cuộc hôn nhân (unit
// cùng primary đứng liền nhau thành nhiều PedigreeNode/`<li>` riêng biệt, xem groupUnitsForRender) -
// KHÔNG áp cho vợ/chồng (luôn là người thật, không phải bản lặp) và KHÔNG áp cho lần xuất hiện ĐẦU
// TIÊN của primary (trông y hệt mọi người khác, chỉ RIÊNG bản lặp mới cần phân biệt - phản hồi thực
// tế "3 cái kia nên trắng, Mai Văn Phần chỉ 1 cái trắng, cái kia màu khác"). KHÔNG vẽ thêm khung/viền
// bao quanh nhiều box rời rạc (dễ vỡ khi tính toán vị trí bằng tay, đã gặp nhiều lần).
// `marriageNote`: ghi chú nhỏ dạng "Hôn nhân N" - CHỈ gắn cho primary khi nhóm có >1 cuộc hôn nhân
// (xem PedigreeNode), chữ nhỏ - chỉ để NHẬN RA, không phải nhãn nổi bật gây chú ý quá mức.
// `elsewhere`: cặp vợ/chồng này nối 2 dòng họ (1 bên có cha/mẹ nhánh A, bên kia có cha/mẹ nhánh B) -
// cây (tree, không phải đồ thị) chỉ đệ quy con cháu được ở ĐÚNG 1 nhánh, nhánh còn lại vẽ LẶP LẠI y
// hệt cặp này (không mất trắng như trước) nhưng KHÔNG có con cháu bên dưới - làm mờ nhẹ + ghi chú nhỏ
// để phân biệt đây là bản phụ, xem đầy đủ ở nhánh kia (xem PedigreeNode).
//
// Phân cấp Ý CHÍNH (tên, thông tin quan trọng nhất) / Ý PHỤ (tuổi, ghi chú hôn nhân) - RÀ SOÁT LẠI
// theo phản hồi thực tế "kiểm tra màu, tương phản, size":
//   - Tên: nâng cỡ chữ lên text-2xs (10px, trước đó 8.4px - bằng với ý phụ, không phân cấp được) +
//     giữ đậm + đen (text-color-000) - tương phản cao nhất, đúng vai trò ý CHÍNH.
//   - Tuổi/ghi chú hôn nhân: giữ nhỏ hơn tên (text-3xs, 8.4px - đúng vai trò ý PHỤ, không giành sự
//     chú ý) nhưng ĐỔI màu từ family-500/family-600 (tương phản đo được ~2.1:1 và ~3.2:1 trên nền
//     trắng - DƯỚI chuẩn tối thiểu WCAG AA cho chữ nhỏ, 4.5:1) sang family-700 (~5:1, đạt chuẩn) -
//     vẫn giữ đúng tông màu cam của app, chỉ đậm thêm 1-2 cấp để đọc được rõ ràng hơn, không đổi hẳn
//     sang xám/đen (tránh lẫn với ý chính). Bỏ chữ nghiêng (italic) ở ghi chú hôn nhân - chữ nghiêng
//     cỡ nhỏ khó đọc hơn, không cần thiết để phân biệt (đã có sẵn dòng riêng + màu khác tên).
export const PedigreeBox = ({person, inLaw, truncated, rotated, tinted, marriageNote, elsewhere, onViewDetail, t}) => {
    const age = computeAge(person.birth_date, person.death_date)
    return (
        <div
            style={{width: rotated ? PEDIGREE_BOX_WIDTH_ROTATED : PEDIGREE_BOX_WIDTH}}
            className={classNames(
                'relative flex-shrink-0 px-1.5 py-1.5 flex flex-col items-center gap-0.5 text-center rounded-lg',
                tinted ? 'bg-family-100' : 'background',
                elsewhere && 'opacity-60',
                inLaw ? 'border border-dashed border-family-200' : 'border border-family-300'
            )}>
            {/* Xoay 90° qua writing-mode (không phải transform:rotate) - chữ tự chạy dọc + tự co
                giãn chiều cao theo độ dài tên, không cần tự tính lại kích thước bằng tay. Cắt bớt
                (ellipsis) khi vượt PEDIGREE_ROTATED_TEXT_MAX_HEIGHT thay vì tràn tự do ra ngoài khung. */}
            <span
                onClick={() => onViewDetail(person.id)}
                style={rotated ? {
                    writingMode: 'vertical-rl',
                    maxHeight: PEDIGREE_ROTATED_TEXT_MAX_HEIGHT,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '0.02em',
                } : undefined}
                className={classNames(
                    'font-bold text-color-000 cursor-pointer hover:underline',
                    rotated ? 'text-xs whitespace-no-wrap' : 'text-2xs leading-snug max-lines-2'
                )}
                title={t('member profile')}>
                {person.name}
            </span>
            <div className={classNames('flex items-center gap-1', rotated && 'flex-col')}>
                <GenderIcon gender={person.gender} size={9} className="text-family-500 flex-shrink-0"/>
                {age != null && (
                    <span className="text-3xs text-family-700 whitespace-no-wrap">
                        {age}{person.death_date ? ' ✝' : ''}
                    </span>
                )}
            </div>
            {/* Cùng quy tắc "1 nhãn trong cây" với PersonBlock - linh mục thì thôi hiện thêm Tu Sĩ. */}
            {person.is_priest ? <PriestBadge small className="mt-0.5"/> : person.is_religious && <ReligiousBadge small className="mt-0.5"/>}
            {marriageNote && !rotated && (
                <span className="text-3xs text-family-700 leading-snug">{marriageNote}</span>
            )}
            {elsewhere && !rotated && (
                <span className="text-3xs text-color-300 italic leading-snug" title={t('pedigree shown in other branch hint')}>
                    {t('shown in other branch')}
                </span>
            )}
            {truncated && <span className="text-3xs text-color-300" title={t('pedigree truncated hint')}>⋯</span>}
        </div>
    )
}

export default PedigreeBox
